import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AdminTable } from '../components/table/AdminTable';
import { ApiPath } from '../api/ApiPath';
import { AdminTableActionsItem } from '../components/table/interfaces/AdminTableActionsItem';
import { AdminToolbar } from '../components/toolbar/AdminToolbar';
import { AdminToolbarTitle } from '../components/toolbar/AdminToolbarTitle';
import { Alert, Button, Modal } from 'react-bootstrap';
import { api } from '../functions/api';
import { useWhatsAppPreferencesColumns } from './hooks/useWhatsAppPreferencesColumns';
import { useApiItems } from '../hooks/useApiItems';
import { WhatsAppUserPreference } from './dto/WhatsAppUserPreference';
import { WhatsAppPreferencesEdit } from './WhatsAppPreferencesEdit';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Customer } from '../customers/dto/Customer';

export const WhatsAppPreferences: FC = () => {
  const { t } = useTranslation();
  const [editablePreference, setEditablePreference] = useState<WhatsAppUserPreference | null>(null);
  const [items, setItems] = useState<WhatsAppUserPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [customers, customersLoading] = useApiItems<Customer>(ApiPath.Customers);
  const columns = useWhatsAppPreferencesColumns();

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await api<WhatsAppUserPreference[]>({ url: ApiPath.WhatsAppPreferences });
      setItems(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);


  const actions = useMemo<AdminTableActionsItem<WhatsAppUserPreference>[]>(
    () => [
      {
        title: t('Edit'),
        onClick: setEditablePreference,
      },
      {
        title: t('Clear Preferences'),
        onClick: async (item) => {
          if (window.confirm(t('Are you sure you want to clear preferences for this user?').toString())) {
            try {
              await api({
                url: ApiPath.WhatsAppPreference.replace(':phoneNumber', encodeURIComponent(item.phoneNumber)),
                method: 'DELETE',
              });
              toast.success(t('Preferences cleared'));
              fetchPreferences();
            } catch (error) {
              toast.error(t('Failed to clear preferences'));
            }
          }
        },
      },
    ],
    [t, fetchPreferences],
  );

  return (
    <>
      <AdminToolbar>
        <AdminToolbarTitle>{t('WhatsApp Bot Users')}</AdminToolbarTitle>
      </AdminToolbar>

      {error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          {t('Failed to load preferences')}
          <Button variant="danger" size="sm" onClick={fetchPreferences}>
            {t('Retry')}
          </Button>
        </Alert>
      )}

      <AdminTable items={items} loading={loading} columns={columns} actions={actions} />

      <Modal show={!!editablePreference} onHide={() => setEditablePreference(null)}>
        {editablePreference && (
          <WhatsAppPreferencesEdit
            preference={editablePreference}
            onClose={() => setEditablePreference(null)}
            onUpdate={fetchPreferences}
            customers={customers}
            customersLoading={customersLoading}
          />
        )}
      </Modal>
    </>
  );
};
